import React from 'react';
import { Plus } from 'lucide-react';
import { FeaturesEditor } from './FeaturesEditor';

/**
 * Pricing tab — 3 cards (Standard plan, Premium plan, Global pricing
 * options) + a disabled "Create pricing plan" ghost placeholder.
 * All fields write back through `onChange({ field: value })` so the
 * parent can compute a single `dirty` flag against `original`.
 */
export const PricingTab = ({ settings, onChange }) => (
  <div className="set-stack">
    {/* ---- Standard plan card ---- */}
    <div className="panel set-card" data-testid="settings-plan-standard">
      <h3 className="admin-info-header">
        Standard plan
        <span className="plan-sub">The entry-level tier · single-chair shops and starters</span>
      </h3>
      <div className="set-row">
        <div className="tx"><b>Monthly price</b><small>What you charge shops per month</small></div>
        <div className="price-inp">
          <span className="pfx">£</span>
          <input
            type="number" min="0" step="1"
            value={settings.standard_price}
            onChange={(e) => onChange({ standard_price: e.target.value })}
            data-testid="settings-standard-price"
          />
        </div>
      </div>
      <div className="set-row">
        <div className="tx"><b>Included barbers</b><small>How many barbers the base price covers</small></div>
        <div className="price-inp">
          <input
            type="number" min="1" max="10" step="1"
            style={{ width: 90 }}
            value={settings.standard_included_barbers ?? 1}
            onChange={(e) => onChange({ standard_included_barbers: e.target.value })}
            data-testid="settings-standard-included"
          />
        </div>
      </div>
      <FeaturesEditor
        label="Features"
        hint="Marketing bullets shown on the Standard plan card"
        testIdPrefix="settings-standard-features"
        value={settings.standard_features || []}
        onChange={(next) => onChange({ standard_features: next })}
      />
    </div>

    {/* ---- Premium plan card ---- */}
    <div className="panel set-card" data-testid="settings-plan-premium">
      <h3 className="admin-info-header">
        Premium plan
        <span className="plan-sub">The full-suite tier · multi-chair shops with add-ons</span>
      </h3>
      <div className="set-row">
        <div className="tx"><b>Monthly price</b><small>What you charge shops per month</small></div>
        <div className="price-inp">
          <span className="pfx">£</span>
          <input
            type="number" min="0" step="1"
            value={settings.premium_price}
            onChange={(e) => onChange({ premium_price: e.target.value })}
            data-testid="settings-premium-price"
          />
        </div>
      </div>
      <div className="set-row">
        <div className="tx"><b>Included barbers</b><small>How many barbers the base price covers</small></div>
        <div className="price-inp">
          <input
            type="number" min="1" max="10" step="1"
            style={{ width: 90 }}
            value={settings.premium_included_barbers ?? 3}
            onChange={(e) => onChange({ premium_included_barbers: e.target.value })}
            data-testid="settings-premium-included"
          />
        </div>
      </div>
      <div className="set-row">
        <div className="tx"><b>Extra barber add-on</b><small>Charge per additional barber over Premium base</small></div>
        <div className="price-inp">
          <span className="pfx">£</span>
          <input
            type="number" min="0" step="1"
            value={settings.extra_barber_price ?? 8}
            onChange={(e) => onChange({ extra_barber_price: e.target.value })}
            data-testid="settings-extra-barber-price"
          />
          <span className="pfx" style={{ borderRight: 'none', borderLeft: '1px solid var(--line)' }}>/mo</span>
        </div>
      </div>
      <FeaturesEditor
        label="Features"
        hint="Marketing bullets shown on the Premium plan card"
        testIdPrefix="settings-premium-features"
        value={settings.premium_features || []}
        onChange={(next) => onChange({ premium_features: next })}
      />
    </div>

    {/* ---- Global pricing options ---- */}
    <div className="panel set-card" data-testid="settings-plan-global">
      <h3 className="admin-info-header">
        Global pricing options
        <span className="plan-sub">Applied across every plan</span>
      </h3>
      <div className="set-row">
        <div className="tx"><b>Annual discount</b><small>Percentage off the equivalent annual price</small></div>
        <div className="price-inp">
          <input
            type="number" min="0" max="100" step="1"
            style={{ width: 70 }}
            value={settings.annual_discount_pct}
            onChange={(e) => onChange({ annual_discount_pct: e.target.value })}
            data-testid="settings-annual-discount"
          />
          <span className="pfx" style={{ borderRight: 'none', borderLeft: '1px solid var(--line)' }}>%</span>
        </div>
      </div>
      <div className="set-row">
        <div className="tx">
          <b>Signup free trial</b>
          <small>Days new shops get before Stripe charges them · card still required at checkout · 0 disables</small>
        </div>
        <div className="price-inp">
          <input
            type="number" min="0" max="365" step="1"
            style={{ width: 90 }}
            value={settings.signup_trial_days ?? 0}
            onChange={(e) => onChange({ signup_trial_days: e.target.value })}
            data-testid="settings-signup-trial-days"
          />
          <span className="pfx" style={{ borderRight: 'none', borderLeft: '1px solid var(--line)' }}>days</span>
        </div>
      </div>
    </div>

    {/* ---- Create-new-plan placeholder ---- */}
    <button
      type="button"
      className="panel set-card ghost"
      disabled
      title="Custom pricing plans are coming soon — schema work required"
      data-testid="settings-create-plan-placeholder"
    >
      <div className="ghost-title"><Plus size={14} /> Create pricing plan</div>
      <div className="ghost-hint">Coming soon — additional tiers will be enabled in a future release.</div>
    </button>
  </div>
);
